import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Alert,
  Divider,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Save as SaveIcon,
  RestartAlt as ResetIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Speed as SpeedIcon,
  Email as EmailIcon,
  Notifications as NotificationsIcon,
  AutoDelete as AutoDeleteIcon,
  Security as SecurityIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { db } from '../../../config/firebase';
import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import {
  NotificationSystemSettings,
  NotificationTypeToggle,
} from '../../../types/notification-admin';
import { NotificationType } from '../../../types/user';

/**
 * Tab panel component
 */
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index} style={{ paddingTop: 24 }}>
      {value === index && children}
    </div>
  );
};

/**
 * NotificationSettings Page
 * Comprehensive system-wide notification configuration
 */
const NotificationSettings: React.FC = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const user = authState.user;

  // Tab state
  const [currentTab, setCurrentTab] = useState(0);

  // Settings state
  const [settings, setSettings] = useState<NotificationSystemSettings | null>(null);
  const [originalSettings, setOriginalSettings] = useState<NotificationSystemSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Type toggle search
  const [typeSearchText, setTypeSearchText] = useState('');
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  /**
   * Load settings on mount
   */
  useEffect(() => {
    loadSettings();
  }, []);

  /**
   * Check for changes
   */
  useEffect(() => {
    if (settings && originalSettings) {
      const changed = JSON.stringify(settings) !== JSON.stringify(originalSettings);
      setHasChanges(changed);
    }
  }, [settings, originalSettings]);

  /**
   * Load settings from Firestore
   */
  const loadSettings = async () => {
    setLoading(true);
    setError(null);

    try {
      const settingsRef = doc(db, 'systemConfig', 'notificationSettings');
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data() as NotificationSystemSettings;
        setSettings(data);
        setOriginalSettings(JSON.parse(JSON.stringify(data)));
      } else {
        // Create default settings
        const defaultSettings = createDefaultSettings();
        setSettings(defaultSettings);
        setOriginalSettings(JSON.parse(JSON.stringify(defaultSettings)));
      }
    } catch (err: any) {
      console.error('Error loading settings:', err);
      setError(err.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Create default settings
   */
  const createDefaultSettings = (): NotificationSystemSettings => {
    return {
      id: 'notificationSettings',
      enabled: true,
      retentionDays: 90,
      autoCleanupEnabled: true,
      cleanupSchedule: 'daily',
      rateLimiting: {
        enabled: true,
        maxPerUserPerHour: 50,
        maxPerUserPerDay: 200,
        cooldownMinutes: 1,
        spamProtectionEnabled: true,
      },
      emailConfig: {
        enabled: false,
        fromAddress: 'noreply@martz.family',
        replyToAddress: 'support@martz.family',
        signature: 'The Martz Family Team',
      },
      typeToggles: getAllNotificationTypeToggles(),
      updatedAt: new Date(),
      updatedBy: user?.id || 'system',
    };
  };

  /**
   * Get all notification type toggles with default enabled state
   */
  const getAllNotificationTypeToggles = (): NotificationTypeToggle[] => {
    const types: NotificationType[] = [
      // Authentication & User Management
      'welcome',
      'email_verified',
      'approval_granted',
      'approval_denied',
      'change_approved',
      'change_rejected',
      'role_changed',
      'account_suspended',
      'account_reactivated',
      'password_reset',
      // Content Moderation
      'content_submitted',
      'content_approved',
      'content_rejected',
      'content_flagged',
      'edit_approved',
      'edit_rejected',
      // Social Interactions
      'comment_mention',
      'comment_reply',
      'comment_on_content',
      'new_comment',
      'photo_tagged',
      'relationship_suggested',
      // Family Tree Events
      'person_added',
      'person_updated',
      'relationship_added',
      'document_uploaded',
      'photo_uploaded',
      'memory_shared',
      'memory_added',
      'document_shared',
      // Research Collaboration
      'research_note_assigned',
      'research_note_updated',
      'research_note_completed',
      'research_request',
      'research_response',
      'collaboration_invite',
      // Reminders & Events
      'birthday_reminder',
      'anniversary_reminder',
      'death_anniversary',
      'task_reminder',
      'profile_incomplete',
      // System & Admin
      'system_announcement',
      'system_update',
      'maintenance_scheduled',
      'feature_announcement',
      'privacy_policy_update',
      'export_ready',
      'import_completed',
      'import_failed',
      'admin_broadcast',
      'custom',
    ];

    return types.map((type) => ({
      type,
      enabled: true,
      description: getTypeDescription(type),
    }));
  };

  /**
   * Get description for notification type
   */
  const getTypeDescription = (type: NotificationType): string => {
    const descriptions: Record<NotificationType, string> = {
      // Authentication & User Management
      welcome: 'Welcome message sent to new users upon registration',
      email_verified: 'Confirmation sent when user verifies their email',
      approval_granted: 'Notification when user account is approved by admin',
      approval_denied: 'Notification when user account is rejected',
      change_approved: 'Notification when user changes are approved',
      change_rejected: 'Notification when user changes are rejected',
      role_changed: 'Notification when user role is modified',
      account_suspended: 'Alert when account is suspended',
      account_reactivated: 'Notification when suspended account is restored',
      password_reset: 'Confirmation of password reset request',
      person_link_requested: 'Alert when user requests to link their account to a person profile',
      person_link_approved: 'Notification when person link request is approved',
      person_link_rejected: 'Notification when person link request is denied',
      person_link_removed: 'Alert when person link is removed by admin',
      // Content Moderation
      content_submitted: 'Notification when content is submitted for review',
      content_approved: 'Notification when uploaded content is approved',
      content_rejected: 'Notification when content is rejected with reason',
      content_flagged: 'Alert when content is flagged for review',
      edit_approved: 'Notification when edits are approved',
      edit_rejected: 'Notification when edits are rejected',
      // Social Interactions
      comment_mention: 'Alert when you are mentioned in a comment',
      comment_reply: 'Notification when someone replies to your comment',
      comment_on_content: 'Alert for comments on your content',
      new_comment: 'Alert for new comments',
      photo_tagged: 'Notification when tagged in a photo',
      relationship_suggested: 'Suggestion for family relationships',
      // Family Tree Events
      person_added: 'Notification when new person is added to tree',
      person_updated: 'Alert when person information is updated',
      relationship_added: 'Alert for new relationships in family tree',
      document_uploaded: 'Alert for new document uploads',
      photo_uploaded: 'Notification of new photo uploads',
      memory_shared: 'Notification when memory is shared',
      memory_added: 'Alert when new memory is added',
      document_shared: 'Notification when document is shared',
      // Research Collaboration
      research_note_assigned: 'Notification when research note is assigned',
      research_note_updated: 'Alert when research note is updated',
      research_note_completed: 'Notification when research note is completed',
      research_request: 'Request for research assistance',
      research_response: 'Response to research request',
      collaboration_invite: 'Invitation to collaborate on project',
      // Reminders & Events
      birthday_reminder: 'Reminders for family birthdays',
      anniversary_reminder: 'Reminders for anniversaries',
      death_anniversary: 'Reminders for death anniversaries',
      task_reminder: 'Reminders for pending tasks',
      profile_incomplete: 'Reminder to complete profile',
      // System & Admin
      system_announcement: 'System-wide announcements',
      system_update: 'Announcements of system updates',
      maintenance_scheduled: 'Alerts for scheduled maintenance',
      feature_announcement: 'Notifications of new features',
      privacy_policy_update: 'Updates to privacy policy',
      export_ready: 'Notification when data export is ready',
      import_completed: 'Notification when import completes',
      import_failed: 'Alert when import fails',
      admin_broadcast: 'Admin broadcast messages',
      custom: 'Custom notification type',
      // Messaging
      new_message: 'Notification for new messages in conversations',
      message_mention: 'Alert when you are mentioned in a message',
      message_reply: 'Notification when someone replies to your message',
      message_reaction: 'Notification when someone reacts to your message',
      group_chat_added: 'Notification when added to a group chat',
      group_chat_removed: 'Notification when removed from a group chat',
      group_chat_name_changed: 'Alert when group chat name is changed',
      group_chat_member_joined: 'Notification when new member joins group',
      group_chat_member_left: 'Notification when member leaves group',
      // Mentions
      note_mention: 'Alert when you are mentioned in a note',
      memory_mention: 'Alert when you are mentioned in a memory',
      feature_request_mention: 'Alert when you are mentioned in a feature request',
    };

    return descriptions[type] || 'Notification type';
  };

  /**
   * Get category for notification type
   */
  const getCategoryForType = (type: NotificationType): string => {
    const categoryMap: Record<NotificationType, string> = {
      // Authentication & User Management
      welcome: 'Authentication & User Management',
      email_verified: 'Authentication & User Management',
      approval_granted: 'Authentication & User Management',
      approval_denied: 'Authentication & User Management',
      change_approved: 'Authentication & User Management',
      change_rejected: 'Authentication & User Management',
      role_changed: 'Authentication & User Management',
      account_suspended: 'Authentication & User Management',
      account_reactivated: 'Authentication & User Management',
      password_reset: 'Authentication & User Management',
      person_link_requested: 'Authentication & User Management',
      person_link_approved: 'Authentication & User Management',
      person_link_rejected: 'Authentication & User Management',
      person_link_removed: 'Authentication & User Management',
      // Content Moderation
      content_submitted: 'Content Moderation',
      content_approved: 'Content Moderation',
      content_rejected: 'Content Moderation',
      content_flagged: 'Content Moderation',
      edit_approved: 'Content Moderation',
      edit_rejected: 'Content Moderation',
      // Social Interactions
      comment_mention: 'Social Interactions',
      comment_reply: 'Social Interactions',
      comment_on_content: 'Social Interactions',
      new_comment: 'Social Interactions',
      photo_tagged: 'Social Interactions',
      relationship_suggested: 'Social Interactions',
      // Family Tree Events
      person_added: 'Family Tree Events',
      person_updated: 'Family Tree Events',
      relationship_added: 'Family Tree Events',
      document_uploaded: 'Family Tree Events',
      photo_uploaded: 'Family Tree Events',
      memory_shared: 'Family Tree Events',
      memory_added: 'Family Tree Events',
      document_shared: 'Family Tree Events',
      // Research Collaboration
      research_note_assigned: 'Research Collaboration',
      research_note_updated: 'Research Collaboration',
      research_note_completed: 'Research Collaboration',
      research_request: 'Research Collaboration',
      research_response: 'Research Collaboration',
      collaboration_invite: 'Research Collaboration',
      // Reminders & Events
      birthday_reminder: 'Reminders & Events',
      anniversary_reminder: 'Reminders & Events',
      death_anniversary: 'Reminders & Events',
      task_reminder: 'Reminders & Events',
      profile_incomplete: 'Reminders & Events',
      // System & Admin
      system_announcement: 'System & Admin',
      system_update: 'System & Admin',
      maintenance_scheduled: 'System & Admin',
      feature_announcement: 'System & Admin',
      privacy_policy_update: 'System & Admin',
      export_ready: 'System & Admin',
      import_completed: 'System & Admin',
      import_failed: 'System & Admin',
      admin_broadcast: 'System & Admin',
      custom: 'System & Admin',
      // Messaging
      new_message: 'Messaging',
      message_mention: 'Messaging',
      message_reply: 'Messaging',
      message_reaction: 'Messaging',
      group_chat_added: 'Messaging',
      group_chat_removed: 'Messaging',
      group_chat_name_changed: 'Messaging',
      group_chat_member_joined: 'Messaging',
      group_chat_member_left: 'Messaging',
      // Mentions
      note_mention: 'Mentions',
      memory_mention: 'Mentions',
      feature_request_mention: 'Mentions',
    };

    return categoryMap[type] || 'Other';
  };

  /**
   * Group type toggles by category
   */
  const getGroupedTypeToggles = (): Record<string, NotificationTypeToggle[]> => {
    if (!settings) return {};

    const grouped: Record<string, NotificationTypeToggle[]> = {};

    settings.typeToggles.forEach((toggle) => {
      const category = getCategoryForType(toggle.type);
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(toggle);
    });

    return grouped;
  };

  /**
   * Filter type toggles by search text
   */
  const filterTypeToggles = (toggles: NotificationTypeToggle[]): NotificationTypeToggle[] => {
    if (!typeSearchText.trim()) return toggles;

    const searchLower = typeSearchText.toLowerCase();
    return toggles.filter(
      (toggle) =>
        toggle.type.toLowerCase().includes(searchLower) ||
        toggle.description.toLowerCase().includes(searchLower)
    );
  };

  /**
   * Handle save settings
   */
  const handleSave = async () => {
    if (!settings || !user) return;

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const settingsRef = doc(db, 'systemConfig', 'notificationSettings');

      const updateData = {
        ...settings,
        updatedAt: serverTimestamp(),
        updatedBy: user.id,
      };

      const settingsDoc = await getDoc(settingsRef);
      if (settingsDoc.exists()) {
        await updateDoc(settingsRef, updateData);
      } else {
        await setDoc(settingsRef, updateData);
      }

      setSuccess('Settings saved successfully');
      setOriginalSettings(JSON.parse(JSON.stringify(settings)));
      setHasChanges(false);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error saving settings:', err);
      setError(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Handle reset to original
   */
  const handleReset = () => {
    if (originalSettings) {
      setSettings(JSON.parse(JSON.stringify(originalSettings)));
      setHasChanges(false);
    }
  };

  /**
   * Handle reset to defaults
   */
  const handleResetToDefaults = () => {
    const defaultSettings = createDefaultSettings();
    setSettings(defaultSettings);
  };

  /**
   * Update settings field
   */
  const updateSettings = (updates: Partial<NotificationSystemSettings>) => {
    if (!settings) return;
    setSettings({ ...settings, ...updates });
  };

  /**
   * Update rate limiting field
   */
  const updateRateLimiting = (updates: Partial<NotificationSystemSettings['rateLimiting']>) => {
    if (!settings) return;
    setSettings({
      ...settings,
      rateLimiting: { ...settings.rateLimiting, ...updates },
    });
  };

  /**
   * Update email config field
   */
  const updateEmailConfig = (updates: Partial<NotificationSystemSettings['emailConfig']>) => {
    if (!settings) return;
    setSettings({
      ...settings,
      emailConfig: { ...settings.emailConfig, ...updates },
    });
  };

  /**
   * Toggle notification type
   */
  const toggleNotificationType = (type: NotificationType, enabled: boolean) => {
    if (!settings) return;

    const updatedToggles = settings.typeToggles.map((toggle) =>
      toggle.type === type ? { ...toggle, enabled } : toggle
    );

    setSettings({ ...settings, typeToggles: updatedToggles });
  };

  /**
   * Toggle all types in category
   */
  const toggleCategory = (category: string, enabled: boolean) => {
    if (!settings) return;

    const updatedToggles = settings.typeToggles.map((toggle) =>
      getCategoryForType(toggle.type) === category ? { ...toggle, enabled } : toggle
    );

    setSettings({ ...settings, typeToggles: updatedToggles });
  };

  /**
   * Get enabled count for category
   */
  const getEnabledCount = (toggles: NotificationTypeToggle[]): number => {
    return toggles.filter((t) => t.enabled).length;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!settings) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">Failed to load settings</Alert>
      </Container>
    );
  }

  const groupedToggles = getGroupedTypeToggles();

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Tooltip title="Back to Notification Dashboard">
            <IconButton onClick={() => navigate('/admin/notifications')} sx={{ mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="h4" sx={{ fontWeight: 700, flexGrow: 1 }}>
            Notification Settings
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 7 }}>
          Configure system-wide notification behavior and preferences
        </Typography>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button
          variant="outlined"
          startIcon={<ResetIcon />}
          onClick={handleReset}
          disabled={!hasChanges || saving}
        >
          Reset
        </Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={!hasChanges || saving}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {hasChanges && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          You have unsaved changes. Click "Save Changes" to apply them.
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={(_, newValue) => setCurrentTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="General Settings" icon={<SettingsIcon />} iconPosition="start" />
          <Tab label="Rate Limiting" icon={<SpeedIcon />} iconPosition="start" />
          <Tab label="Email Configuration" icon={<EmailIcon />} iconPosition="start" />
          <Tab label="Type Toggles" icon={<NotificationsIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* General Settings Tab */}
      <TabPanel value={currentTab} index={0}>
        <Grid container spacing={3}>
          {/* Master Toggle Card */}
          <Grid item xs={12}>
            <Card>
              <CardHeader
                avatar={<NotificationsIcon />}
                title="Master Notification Toggle"
                subheader="Enable or disable all system notifications"
              />
              <CardContent>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.enabled}
                      onChange={(e) => updateSettings({ enabled: e.target.checked })}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight={500}>
                        Notifications Enabled
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        When disabled, no notifications will be sent to users
                      </Typography>
                    </Box>
                  }
                />

                {!settings.enabled && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      All notifications are currently disabled system-wide. Users will not receive
                      any notifications until this is re-enabled.
                    </Typography>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Retention Policy Card */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardHeader
                avatar={<AutoDeleteIcon />}
                title="Retention Policy"
                subheader="Configure how long notifications are stored"
              />
              <CardContent>
                <Stack spacing={3}>
                  <TextField
                    label="Retention Period (Days)"
                    type="number"
                    value={settings.retentionDays}
                    onChange={(e) =>
                      updateSettings({ retentionDays: parseInt(e.target.value) || 90 })
                    }
                    fullWidth
                    InputProps={{
                      endAdornment: <InputAdornment position="end">days</InputAdornment>,
                    }}
                    helperText="Notifications older than this will be eligible for deletion"
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.autoCleanupEnabled}
                        onChange={(e) => updateSettings({ autoCleanupEnabled: e.target.checked })}
                      />
                    }
                    label="Enable Auto-Cleanup"
                  />

                  {settings.autoCleanupEnabled && (
                    <FormControl fullWidth>
                      <InputLabel>Cleanup Schedule</InputLabel>
                      <Select
                        value={settings.cleanupSchedule}
                        label="Cleanup Schedule"
                        onChange={(e) =>
                          updateSettings({
                            cleanupSchedule: e.target.value as 'daily' | 'weekly' | 'monthly',
                          })
                        }
                      >
                        <MenuItem value="daily">Daily</MenuItem>
                        <MenuItem value="weekly">Weekly</MenuItem>
                        <MenuItem value="monthly">Monthly</MenuItem>
                      </Select>
                    </FormControl>
                  )}

                  <Alert severity="info">
                    <Typography variant="caption">
                      Read notifications older than {settings.retentionDays} days will be
                      automatically deleted
                      {settings.autoCleanupEnabled ? ` ${settings.cleanupSchedule}` : ' manually'}.
                    </Typography>
                  </Alert>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Security Card */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardHeader
                avatar={<SecurityIcon />}
                title="Security & Privacy"
                subheader="Configure security-related settings"
              />
              <CardContent>
                <Stack spacing={2}>
                  <Alert severity="info">
                    <Typography variant="body2">
                      All notifications are user-specific and can only be accessed by the recipient
                      user or administrators.
                    </Typography>
                  </Alert>

                  <Divider />

                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Data Privacy
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Notifications are encrypted in transit
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Personal data is never shared with third parties
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Users can delete their own notifications
                    </Typography>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Firestore Security
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Firestore rules enforce that users can only read and update their own
                      notifications. Administrators have full access for support purposes.
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Actions Card */}
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Advanced Actions" subheader="Reset settings to default values" />
              <CardContent>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<ResetIcon />}
                    onClick={handleResetToDefaults}
                  >
                    Reset to Defaults
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    This will reset all settings to their default values. You must save to apply.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Rate Limiting Tab */}
      <TabPanel value={currentTab} index={1}>
        <Grid container spacing={3}>
          {/* Rate Limiting Card */}
          <Grid item xs={12}>
            <Card>
              <CardHeader
                avatar={<SpeedIcon />}
                title="Rate Limiting Configuration"
                subheader="Prevent notification spam and abuse"
              />
              <CardContent>
                <Stack spacing={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.rateLimiting.enabled}
                        onChange={(e) => updateRateLimiting({ enabled: e.target.checked })}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" fontWeight={500}>
                          Enable Rate Limiting
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Limit the number of notifications each user can receive
                        </Typography>
                      </Box>
                    }
                  />

                  {settings.rateLimiting.enabled && (
                    <>
                      <Divider />

                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Max Per User Per Hour"
                            type="number"
                            value={settings.rateLimiting.maxPerUserPerHour}
                            onChange={(e) =>
                              updateRateLimiting({
                                maxPerUserPerHour: parseInt(e.target.value) || 50,
                              })
                            }
                            fullWidth
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">notifications/hour</InputAdornment>
                              ),
                            }}
                            helperText="Maximum notifications per user per hour"
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Max Per User Per Day"
                            type="number"
                            value={settings.rateLimiting.maxPerUserPerDay}
                            onChange={(e) =>
                              updateRateLimiting({
                                maxPerUserPerDay: parseInt(e.target.value) || 200,
                              })
                            }
                            fullWidth
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">notifications/day</InputAdornment>
                              ),
                            }}
                            helperText="Maximum notifications per user per day"
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Cooldown Period"
                            type="number"
                            value={settings.rateLimiting.cooldownMinutes}
                            onChange={(e) =>
                              updateRateLimiting({
                                cooldownMinutes: parseInt(e.target.value) || 1,
                              })
                            }
                            fullWidth
                            InputProps={{
                              endAdornment: <InputAdornment position="end">minutes</InputAdornment>,
                            }}
                            helperText="Minimum time between duplicate notifications"
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={settings.rateLimiting.spamProtectionEnabled}
                                onChange={(e) =>
                                  updateRateLimiting({ spamProtectionEnabled: e.target.checked })
                                }
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body2" fontWeight={500}>
                                  Spam Protection
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Block rapid-fire duplicate notifications
                                </Typography>
                              </Box>
                            }
                          />
                        </Grid>
                      </Grid>

                      <Alert severity="info">
                        <Typography variant="body2">
                          <strong>Current Limits:</strong>
                        </Typography>
                        <Typography variant="body2">
                          • Maximum {settings.rateLimiting.maxPerUserPerHour} notifications per user
                          per hour
                        </Typography>
                        <Typography variant="body2">
                          • Maximum {settings.rateLimiting.maxPerUserPerDay} notifications per user
                          per day
                        </Typography>
                        <Typography variant="body2">
                          • {settings.rateLimiting.cooldownMinutes}-minute cooldown between duplicate
                          notifications
                        </Typography>
                      </Alert>
                    </>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Rate Limit Info */}
          <Grid item xs={12}>
            <Card>
              <CardHeader
                avatar={<InfoIcon />}
                title="How Rate Limiting Works"
                subheader="Understanding notification throttling"
              />
              <CardContent>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Per-User Limits
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Each user has individual hourly and daily limits. When a user reaches their
                      limit, new notifications are queued or dropped based on priority.
                    </Typography>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Cooldown Period
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Prevents duplicate notifications within the specified time window. For
                      example, if a user receives a "new_comment" notification, they won't receive
                      another identical notification for the same content within the cooldown
                      period.
                    </Typography>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Spam Protection
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      When enabled, the system monitors for suspicious patterns like multiple
                      identical notifications in rapid succession and automatically blocks them.
                    </Typography>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Priority Override
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Critical and high-priority notifications can bypass rate limits to ensure
                      important alerts are always delivered.
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Email Configuration Tab */}
      <TabPanel value={currentTab} index={2}>
        <Grid container spacing={3}>
          {/* Email Settings Card */}
          <Grid item xs={12}>
            <Card>
              <CardHeader
                avatar={<EmailIcon />}
                title="Email Notification Settings"
                subheader="Configure email delivery for notifications"
              />
              <CardContent>
                <Stack spacing={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.emailConfig.enabled}
                        onChange={(e) => updateEmailConfig({ enabled: e.target.checked })}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" fontWeight={500}>
                          Enable Email Notifications
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Send email copies of in-app notifications
                        </Typography>
                      </Box>
                    }
                  />

                  {settings.emailConfig.enabled && (
                    <>
                      <Divider />

                      <TextField
                        label="From Address"
                        type="email"
                        value={settings.emailConfig.fromAddress}
                        onChange={(e) => updateEmailConfig({ fromAddress: e.target.value })}
                        fullWidth
                        helperText="Email address notifications will be sent from"
                      />

                      <TextField
                        label="Reply-To Address"
                        type="email"
                        value={settings.emailConfig.replyToAddress}
                        onChange={(e) => updateEmailConfig({ replyToAddress: e.target.value })}
                        fullWidth
                        helperText="Email address for user replies"
                      />

                      <TextField
                        label="Email Signature"
                        multiline
                        rows={3}
                        value={settings.emailConfig.signature}
                        onChange={(e) => updateEmailConfig({ signature: e.target.value })}
                        fullWidth
                        helperText="Signature appended to all notification emails"
                      />

                      <Alert severity="warning">
                        <Typography variant="body2">
                          <strong>Note:</strong> Email notifications require SMTP configuration in
                          your Firebase project. Make sure you have configured Firebase email
                          extensions or a third-party email service.
                        </Typography>
                      </Alert>
                    </>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Email Preview */}
          {settings.emailConfig.enabled && (
            <Grid item xs={12}>
              <Card>
                <CardHeader title="Email Preview" subheader="Example notification email" />
                <CardContent>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      bgcolor: 'background.default',
                      border: 1,
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                      From: {settings.emailConfig.fromAddress}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                      Reply-To: {settings.emailConfig.replyToAddress}
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="h6" gutterBottom>
                      New Comment on Your Photo
                    </Typography>
                    <Typography variant="body1" paragraph>
                      John Smith commented on your photo "Family Reunion 2024": "What a wonderful
                      gathering! Thanks for sharing these memories."
                    </Typography>

                    <Button variant="contained" size="small">
                      View Comment
                    </Button>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                      {settings.emailConfig.signature}
                    </Typography>
                  </Paper>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </TabPanel>

      {/* Type Toggles Tab */}
      <TabPanel value={currentTab} index={3}>
        <Grid container spacing={3}>
          {/* Search and Info */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Stack spacing={2}>
                  <TextField
                    label="Search Notification Types"
                    value={typeSearchText}
                    onChange={(e) => setTypeSearchText(e.target.value)}
                    fullWidth
                    placeholder="Search by type name or description..."
                  />

                  <Alert severity="info">
                    <Typography variant="body2">
                      Enable or disable specific notification types. Disabled types will not be sent
                      to any users, regardless of their personal preferences.
                    </Typography>
                  </Alert>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Type Toggles by Category */}
          {Object.entries(groupedToggles).map(([category, toggles]) => {
            const filteredToggles = filterTypeToggles(toggles);
            if (filteredToggles.length === 0 && typeSearchText.trim()) return null;

            const enabledCount = getEnabledCount(filteredToggles);
            const allEnabled = enabledCount === filteredToggles.length;
            const someEnabled = enabledCount > 0 && enabledCount < filteredToggles.length;

            return (
              <Grid item xs={12} key={category}>
                <Accordion
                  expanded={expandedCategory === category}
                  onChange={() =>
                    setExpandedCategory(expandedCategory === category ? null : category)
                  }
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        pr: 2,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="h6">{category}</Typography>
                        <Chip
                          label={`${enabledCount}/${filteredToggles.length} enabled`}
                          size="small"
                          color={allEnabled ? 'success' : someEnabled ? 'warning' : 'default'}
                        />
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1 }} onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Enable all in category">
                          <IconButton
                            size="small"
                            onClick={() => toggleCategory(category, true)}
                            disabled={allEnabled}
                          >
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Disable all in category">
                          <IconButton
                            size="small"
                            onClick={() => toggleCategory(category, false)}
                            disabled={enabledCount === 0}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails>
                    <List>
                      {filteredToggles.map((toggle, index) => (
                        <React.Fragment key={toggle.type}>
                          <ListItem>
                            <ListItemText
                              primary={
                                <Typography variant="body1" fontWeight={500}>
                                  {toggle.type}
                                </Typography>
                              }
                              secondary={
                                <Typography variant="body2" color="text.secondary">
                                  {toggle.description}
                                </Typography>
                              }
                            />
                            <ListItemSecondaryAction>
                              <Switch
                                checked={toggle.enabled}
                                onChange={(e) =>
                                  toggleNotificationType(toggle.type, e.target.checked)
                                }
                              />
                            </ListItemSecondaryAction>
                          </ListItem>
                          {index < filteredToggles.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            );
          })}
        </Grid>
      </TabPanel>

      {/* Bottom Save Button */}
      {hasChanges && (
        <Box
          sx={{
            position: 'sticky',
            bottom: 16,
            display: 'flex',
            justifyContent: 'center',
            mt: 3,
          }}
        >
          <Paper
            elevation={6}
            sx={{
              p: 2,
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              bgcolor: 'warning.light',
            }}
          >
            <WarningIcon color="warning" />
            <Typography variant="body2" fontWeight={500}>
              You have unsaved changes
            </Typography>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button variant="outlined" onClick={handleReset} disabled={saving}>
              Discard
            </Button>
          </Paper>
        </Box>
      )}
    </Container>
  );
};

export default NotificationSettings;
